import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../utils/scrollLock';

interface ModalProps {
    open: boolean;
    title: ReactNode;
    /** 标题下的次要说明行 (对齐原型 modalHead 的 sub 参数) */
    subtitle?: ReactNode;
    onClose: () => void;
    children: ReactNode;
    width?: number;
    /** 内容默认带 p-5 内边距; 需要通栏内容 (如满宽表格) 时置 true 自行控制 */
    noPadding?: boolean;
}

// 通用模态框, 完全对齐原型 #modal 的实现:
//   - 半透明遮罩 (rgba(15,23,42,.45))
//   - 居中卡片, 圆角 + 阴影 + 最大高度 88vh 滚动
//   - Header: 标题 [+ 副标题] + 右上角 ✕
//   - ESC 关闭 / 点击遮罩关闭
//   - portal 挂到 body (2026-10-19): 原先渲染在页面容器里, 页面外层的 space-y-* 会给它加 margin-top,
//     fixed 遮罩被顶下 20px, 顶部露出一条没盖住的白边 (全平台弹窗都有); 挂到 body 就不受容器样式影响.
const Modal = ({ open, title, subtitle, onClose, children, width = 720, noPadding }: ModalProps) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        // 锁定 body 滚动 (引用计数, 见 utils/scrollLock: 弹层叠加时不能各自存/还原原值)
        const unlock = lockBodyScroll();
        return () => {
            document.removeEventListener('keydown', onKey);
            unlock();
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,.45)' }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-xl max-h-[88vh] overflow-auto"
                style={{ width, maxWidth: '100%' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                    <div>
                        <div className="text-base font-semibold">{title}</div>
                        {subtitle && (
                            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 text-lg leading-none"
                        aria-label="关闭"
                    >
                        ✕
                    </button>
                </div>
                {noPadding ? children : <div className="p-5">{children}</div>}
            </div>
        </div>,
        document.body,
    );
};

export default Modal;
